import {
    Table,
    Input,
    Select,
    DatePicker,
    Tag,
    Button,
    Space,
    Tooltip,
    Popconfirm,
} from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
    RequestDeleteUser,
    RequestGetAllUsers,
    RequestCreateUser,
    RequestUpdateUser,
} from "../../Actions/user.action";
import { fetchAllClients } from "../../Actions/client.action";
import UserFormModal from "./UserFormModal";
import { useTranslation } from "react-i18next";
import "./utilisateurs.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

const UserTablePage = () => {
    const { t } = useTranslation("users");
    const dispatch = useDispatch();

    const { users = [], loading } = useSelector((state) => state.users || {});
    const { clients: clientList = [] } = useSelector((state) => state.clients || {});

    const currentUser = useSelector((state) => {
        const slice = state.users || state.user || {};
        return slice.currentUser && Object.keys(slice.currentUser).length
            ? slice.currentUser
            : slice.user && Object.keys(slice.user).length
                ? slice.user
                : null;
    });

    const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";
    const isAdminClient = currentUser?.role === "ADMIN_CLIENT";

    const sortedClientList = useMemo(() => {
        return [...clientList].sort((a, b) => {
            const aName = (a?.name || "").toLowerCase();
            const bName = (b?.name || "").toLowerCase();
            return aName.localeCompare(bName, "fr");
        });
    }, [clientList]);

    const canEditUser = (record) => {
        if (record.is_protected) return false;
        if (isSuperAdmin) return true;
        if (isAdminClient && (record.role === "SUPER_ADMIN" || record.role === "ADMIN_CLIENT")) return false;
        return true;
    };

    const canDeleteUser = (record) => {
        if (record.is_protected) return false;
        if (isSuperAdmin) return true;
        if (isAdminClient && (record.role === "SUPER_ADMIN" || record.role === "ADMIN_CLIENT")) return false;
        if (record.id === currentUser?.id) return false;
        return true;
    };

    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState(undefined);
    const [statusFilter, setStatusFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState(null);
    const [clientFilter, setClientFilter] = useState(undefined);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    useEffect(() => {
        dispatch(RequestGetAllUsers());
        if (isSuperAdmin) {
            dispatch(fetchAllClients());
        }
    }, [dispatch, isSuperAdmin]);

    const filteredData = useMemo(() => {
        return users.filter((user) => {
            const searchValue = search.toLowerCase();
            const matchesSearch =
                !search ||
                (user.name || "").toLowerCase().includes(searchValue) ||
                (user.surname || "").toLowerCase().includes(searchValue) ||
                (user.email || "").toLowerCase().includes(searchValue);

            const matchesRole = !roleFilter || user.role === roleFilter;
            const matchesStatus =
                statusFilter === undefined || user.is_active === statusFilter;
            const matchesClient = !clientFilter || user.client_id === clientFilter;
            const matchesDate =
                !dateRange ||
                moment(user.created_at).isBetween(dateRange[0], dateRange[1], "day", "[]");

            return matchesSearch && matchesRole && matchesStatus && matchesClient && matchesDate;
        });
    }, [users, search, roleFilter, statusFilter, clientFilter, dateRange]);

    const openCreateModal = () => {
        setEditingUser(null);
        setIsModalVisible(true);
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setIsModalVisible(true);
    };

    const closeModal = () => {
        setIsModalVisible(false);
        setEditingUser(null);
    };

    const normalizePayload = useCallback(
        (formValues) => {
            const payload = { ...formValues };
            const isEditing = !!editingUser;
            const isSelfEdit = isEditing && currentUser?.id && editingUser?.id === currentUser.id;
            delete payload.max_token;

            if (!isSuperAdmin) {
                payload.role = "USER";
                if (currentUser?.client_id) payload.client_id = currentUser.client_id;
            }

            if (isSelfEdit) {
                delete payload.role;
                delete payload.client_id;
            }

            return payload;
        },
        [editingUser, currentUser, isSuperAdmin]
    );

    const handleSubmitUser = async (formValues) => {
        const safePayload = normalizePayload(formValues);

        let result;
        if (editingUser) {
            result = await dispatch(RequestUpdateUser(editingUser.id, safePayload));
        } else {
            result = await dispatch(RequestCreateUser(safePayload));
        }
        if (result?.success !== false) {
            closeModal();
        }
    };

    const columns = [
        {
            title: t("table.name"),
            dataIndex: "name",
            width: 250,
            render: (_, record) => (
                <span className="users-table__name">
                    <span>{`${record.name || ""} ${record.surname || ""}`.trim() || "-"}</span>
                    {record.is_protected && (
                        <Tag className="users-status users-status--protected">
                            {t("tags.protected")}
                        </Tag>
                    )}
                </span>
            ),
        },
        {
            title: t("table.email"),
            dataIndex: "email",
            width: 270,
            render: (email) => <span className="users-table__muted">{email || "-"}</span>,
        },
        {
            title: t("table.client"),
            dataIndex: "client_name",
            width: 180,
            render: (client) => <span>{client || "-"}</span>,
        },
        {
            title: t("table.role"),
            dataIndex: "role",
            width: 150,
            render: (role) => (
                <Tag className={`users-role users-role--${role || "default"}`}>
                    {t(`roles.${role}`)}
                </Tag>
            ),
        },
        {
            title: t("table.status"),
            dataIndex: "is_active",
            width: 120,
            render: (active) => (
                <Tag className={active ? "users-status users-status--active" : "users-status users-status--inactive"}>
                    {active ? t("status.active") : t("status.inactive")}
                </Tag>
            ),
        },
        {
            title: t("table.maxTokens"),
            dataIndex: "max_token",
            width: 130,
            render: (value) => <span className="users-table__muted">{value ?? "-"}</span>,
        },
        {
            title: t("table.lastLogin"),
            dataIndex: "last_login",
            width: 175,
            render: (date) => (
                <span className="users-table__muted">
                    {date ? moment(date).format("DD/MM/YYYY HH:mm") : "-"}
                </span>
            ),
        },
        {
            title: t("table.createdAt"),
            dataIndex: "created_at",
            width: 135,
            render: (date) => moment(date).format("DD/MM/YYYY"),
            sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
        },
        {
            title: t("table.actions"),
            key: "actions",
            width: 110,
            fixed: "right",
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title={canEditUser(record) ? t("actions.edit") : t("actions.protectedEdit")}>
                        <Button
                            type="text"
                            className="users-action-btn"
                            icon={<EditOutlined />}
                            disabled={!canEditUser(record)}
                            onClick={() => {
                                if (canEditUser(record)) openEditModal(record);
                            }}
                        />
                    </Tooltip>

                    {!canDeleteUser(record) ? (
                        <Tooltip title={record.is_protected ? t("actions.protectedDelete") : t("actions.protectedDelete")}>
                            <Button
                                type="text"
                                className="users-action-btn users-action-btn--danger"
                                icon={<DeleteOutlined />}
                                danger
                                disabled
                            />
                        </Tooltip>
                    ) : (
                        <Popconfirm
                            title={t("actions.confirmDelete")}
                            okText={t("actions.confirm")}
                            cancelText={t("actions.cancel")}
                            onConfirm={() => dispatch(RequestDeleteUser(record.id))}
                        >
                            <Tooltip title={t("actions.delete")}>
                                <Button
                                    type="text"
                                    className="users-action-btn users-action-btn--danger"
                                    icon={<DeleteOutlined />}
                                    danger
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="users-page">
            <div className="users-page__header">
                <p className="users-page__subtitle">
                    {filteredData.length} {t("labels.displayed")}
                </p>
                <div className="users-page__actions">
                    <Button
                        type="primary"
                        className="users-page__primary-btn verdictia-primary-btn"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                    >
                        {t("actions.add")}
                    </Button>
                </div>
            </div>

            <div className="users-page__filters">
                <div className="users-page__filter users-page__filter--search">
                    <Input
                        placeholder={t("search.placeholder")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                </div>

                <div className="users-page__filter">
                    <Select
                        placeholder={t("search.filterRole")}
                        value={roleFilter}
                        onChange={(value) => setRoleFilter(value)}
                        allowClear
                        style={{ width: "100%" }}
                    >
                        <Option value="SUPER_ADMIN">{t("roles.SUPER_ADMIN")}</Option>
                        <Option value="ADMIN_CLIENT">{t("roles.ADMIN_CLIENT")}</Option>
                        <Option value="USER">{t("roles.USER")}</Option>
                    </Select>
                </div>

                <div className="users-page__filter">
                    <Select
                        placeholder={t("search.filterStatus")}
                        value={statusFilter}
                        onChange={(value) => setStatusFilter(value)}
                        allowClear
                        style={{ width: "100%" }}
                    >
                        <Option value={true}>{t("status.active")}</Option>
                        <Option value={false}>{t("status.inactive")}</Option>
                    </Select>
                </div>

                <div className="users-page__filter">
                    <Select
                        placeholder={t("search.filterClient")}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            String(option?.children || "")
                                .toLowerCase()
                                .includes((input || "").toLowerCase())
                        }
                        value={clientFilter}
                        onChange={(value) => setClientFilter(value)}
                        allowClear
                        style={{ width: "100%" }}
                    >
                        {sortedClientList.map((client) => (
                            <Option key={client.id} value={client.id}>
                                {client.name}
                            </Option>
                        ))}
                    </Select>
                </div>

                <div className="users-page__filter">
                    <RangePicker
                        style={{ width: "100%" }}
                        onChange={(dates) => setDateRange(dates)}
                        placeholder={[t("search.createdAfter"), t("search.createdBefore")]}
                    />
                </div>
            </div>

            <div className="users-page__table-card">
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    size="middle"
                    pagination={{ pageSize: 8, showSizeChanger: false }}
                    scroll={{ x: 1520 }}
                    tableLayout="fixed"
                    rowClassName={(record) => (!record.is_active ? "inactive-row" : "")}
                    rowKey={(r) => r.id}
                />
            </div>

            <UserFormModal
                open={isModalVisible}
                onClose={closeModal}
                onSubmit={handleSubmitUser}
                initialValues={editingUser}
                clientOptions={isSuperAdmin ? clientList : []}
                currentUser={currentUser}
            />
        </div>
    );
};

export default UserTablePage;
